// The cart builder's Custom Website section: which cart lines a deposit or a
// balance adds, and what has to be true before the rep can add one.
//
// The lines come from squish-backend (lib/custom-website-catalog.js
// phaseLines, served at /api/v2/internal/catalogs/custom-website and passed
// through /api/products/live as `customWebsite`). This file only picks a
// package's served lines, so the CRM cart and the /websites form's deposit
// cart are the same cart. Prices on the lines are display estimates; checkout
// re-prices every line server-side.
//
// A BALANCE names its deposit's engagement (metadata.engagementId). Paying it
// flips that engagement to Delivered. Without the id, fulfilment writes a
// second, standalone row and the real build sits "In build" forever, so the
// section never adds a balance without one.

export const CW_BUILD_LINE = 'cw-build';
export const CW_NOOK_LINE = 'cw-nook';
const BUILD_CODE = 'CUSTOM_WEBSITE_BUILD';

// The account's Custom Website builds a balance can pay off: deposit paid,
// not yet delivered. From the account's products (listAccountProducts), which
// carry the professional-services adapter's rows.
export function buildsAwaitingBalance(products) {
  return (Array.isArray(products) ? products : []).filter((p) =>
    p?.productKind === 'professional-services'
    && /^custom-website-/.test(p.sku || '')
    && p.state === 'IN_BUILD');
}

// The cart lines for one phase, or null when the catalog cannot build them.
//   deposit: { sku, includeNook }   the Nook is the first-month-included bundle
//   balance: { engagement }         the build's own sku sets the price
export function customWebsiteLines(cwCatalog, { phase, sku, includeNook = true, engagement = null }) {
  const tierSku = phase === 'balance' ? engagement?.sku : sku;
  const served = cwCatalog?.tiers?.[tierSku]?.lines?.[phase];
  if (!Array.isArray(served) || served.length === 0) return null;
  if (phase === 'balance' && !engagement?.productId) return null;
  const out = [];
  for (const line of served) {
    const isBuild = line.productCode === BUILD_CODE;
    if (!isBuild && (phase !== 'deposit' || !includeNook)) continue;
    const metadata = { ...line.metadata };
    if (isBuild && phase === 'balance') metadata.engagementId = String(engagement.productId);
    out.push({ ...line, lineId: isBuild ? CW_BUILD_LINE : CW_NOOK_LINE, metadata });
  }
  return out.some((l) => l.lineId === CW_BUILD_LINE) ? out : null;
}

// One build per cart: adding a phase replaces whatever Custom Website lines
// the cart already has, and keeps everything else in order.
export function withCustomWebsite(items, lines) {
  const rest = (items || []).filter((i) => i.lineId !== CW_BUILD_LINE && i.lineId !== CW_NOOK_LINE);
  return [...rest, ...lines];
}

// Removing the build takes its bundled Nook with it: checkout refuses a
// first-month-included Nook with no deposit beside it.
export function withoutLine(items, lineId) {
  const drop = lineId === CW_BUILD_LINE ? new Set([CW_BUILD_LINE, CW_NOOK_LINE]) : new Set([lineId]);
  return (items || []).filter((i) => !drop.has(i.lineId));
}

// Why the rep can't add this phase right now (null = addable).
//   builds: null while loading, { error } when the account's products failed,
//           otherwise the array from buildsAwaitingBalance.
export function customWebsiteBlocker({ cwCatalog, isInvoice, isProspect, phase, builds }) {
  if (!cwCatalog?.tiers) return 'Custom Website catalog unavailable';
  if (isInvoice) return 'Card checkout only: switch to Card to sell a build';
  if (phase !== 'balance') return null;
  if (isProspect) return 'A balance pays off a deposit, so it needs the customer\'s account';
  if (builds == null) return 'Loading this account\'s builds…';
  if (builds.error) return `Couldn't load this account's builds: ${builds.error}`;
  if (builds.length === 0) return 'No build in progress on this account. A balance pays off a paid deposit.';
  return null;
}
